import { Box, Button, Checkbox, FormControlLabel, FormHelperText, IconButton, makeStyles, TextField, Typography } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import RemoveIcon from '@material-ui/icons/Remove'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'
import axios from 'axios'
import jwtDecode from 'jwt-decode'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import AsyncSelect from 'react-select/async'
import formatVehicleLabel from './formatVehicleLabel'
import ReservationSuggestions from './ReservationSuggestions'
import { office_location } from '../../modules/shared/enums/office_location'

const physicalDestinations = office_location.filter((location) => !location.value.startsWith('HO-'))
const normalizeDestination = (value) => (typeof value === 'string' ? value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase() : '')
const selectedDestination = (value) =>
  physicalDestinations.find((location) => normalizeDestination(location.value) === normalizeDestination(value))?.value || ''

const useStyles = makeStyles((theme) => ({
  form: {
    '& .MuiButtonBase-root.Mui-disabled, & .MuiInputBase-root.Mui-disabled, & .reservation-participants__control--is-disabled': {
      opacity: 0.5
    },
    '& .MuiInputBase-root.Mui-disabled .MuiButtonBase-root.Mui-disabled': {
      opacity: 1
    }
  },
  fields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(3),
    marginTop: theme.spacing(2),
    '& .MuiOutlinedInput-notchedOutline': {
      borderBottom: '1px solid rgba(0, 0, 0, 0.23)'
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)'
    }
  },
  fullWidth: {
    gridColumn: '1 / -1'
  },
  participantLabel: {
    display: 'block',
    marginBottom: theme.spacing(1)
  },
  vehicleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    '& + &': {
      marginTop: theme.spacing(2)
    }
  },
  vehicleAction: {
    marginTop: theme.spacing(1)
  },
  vehicleAddAction: {
    alignSelf: 'stretch',
    width: theme.spacing(7),
    flexShrink: 0,
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      borderColor: theme.palette.text.primary
    }
  },
  error: {
    marginTop: theme.spacing(2)
  }
}))

const getCurrentUser = () => {
  try {
    return jwtDecode(localStorage.getItem('token'))
  } catch {
    return {}
  }
}

const getAvailabilityKey = (form, reservation = null) => {
  const fields = ['departureDate', 'departureTime', 'returnDate', 'returnTime']
  if (fields.some((field) => !form[field] || !moment(form[field]).isValid())) return ''
  const schedule = Object.fromEntries(
    fields.map((field) => [field, moment(form[field]).format(field.endsWith('Date') ? 'YYYY-MM-DD' : 'HH:mm')])
  )
  const departure = moment(`${schedule.departureDate} ${schedule.departureTime}`, 'YYYY-MM-DD HH:mm', true)
  const returnAt = moment(`${schedule.returnDate} ${schedule.returnTime}`, 'YYYY-MM-DD HH:mm', true)
  const unchangedDeparture =
    reservation && schedule.departureDate === reservation.departureDate && schedule.departureTime === reservation.departureTime
  if (
    (!unchangedDeparture &&
      (departure.isBefore(moment().startOf('minute')) || departure.isAfter(moment().startOf('day').add(5, 'days'), 'day'))) ||
    !returnAt.isAfter(departure)
  )
    return ''
  return JSON.stringify(schedule)
}

const ReservationRequestForm = ({ onClose, onSaved, onJoined, reservation = null, onReloadReservation }) => {
  const classes = useStyles()
  const savingRef = useRef(false)
  const [currentUser] = useState(getCurrentUser)
  const isEditing = Boolean(reservation?._id)
  const ownerId = isEditing ? reservation.owner?._id : currentUser.user_id
  const [ownerName, setOwnerName] = useState(
    isEditing
      ? [reservation.owner?.first_name, reservation.owner?.last_name].filter(Boolean).join(' ') || reservation.owner?.email || ''
      : currentUser.email || ''
  )
  const [ownerLoading, setOwnerLoading] = useState(false)
  const [ownerError, setOwnerError] = useState('')
  const [participantError, setParticipantError] = useState('')
  const [form, setForm] = useState({
    departureDate: reservation ? moment(reservation.departureDate, 'YYYY-MM-DD', true).toDate() : null,
    departureTime: reservation ? moment(reservation.departureTime, 'HH:mm', true).toDate() : null,
    returnDate: reservation ? moment(reservation.returnDate, 'YYYY-MM-DD', true).toDate() : null,
    returnTime: reservation ? moment(reservation.returnTime, 'HH:mm', true).toDate() : null,
    destination: selectedDestination(reservation?.destination),
    vehicleIds: reservation?.vehicles?.map((vehicle) => vehicle.vehicle) || [''],
    participants:
      reservation?.participants?.map((person) => ({
        value: person._id,
        label: `${person.first_name} ${person.last_name}${person.email ? ` (${person.email})` : ''}`
      })) || [],
    exceptionalCase: reservation?.exceptionalCase || false,
    exceptionReason: reservation?.exceptionReason || ''
  })
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')
  const [availability, setAvailability] = useState({ key: '', vehicles: [], loading: false, error: '' })
  const [availabilityReload, setAvailabilityReload] = useState(0)
  const [suggestions, setSuggestions] = useState({ key: '', data: null, loading: false, error: '' })
  const [suggestionsReload, setSuggestionsReload] = useState(0)
  const [joiningId, setJoiningId] = useState(null)
  const availabilityKey = getAvailabilityKey(form, reservation)
  const managementPath = isEditing ? `vehicle-reservations/manage/${reservation._id}` : ''
  const availabilityReady = Boolean(availabilityKey) && availability.key === availabilityKey && !availability.loading && !availability.error
  const vehicles = availabilityReady ? availability.vehicles : []
  const availableVehicles = vehicles.filter((vehicle) => vehicle.available === true)
  const vehiclesLoading = Boolean(availabilityKey) && (availability.key !== availabilityKey || availability.loading)
  const vehiclesError = availability.key === availabilityKey ? availability.error : ''
  const today = moment().startOf('day')
  const lastBookingDate = today.clone().add(5, 'days')
  const selectedVehicleIds = form.vehicleIds.filter(Boolean)
  const selectedVehicles = availableVehicles.filter((vehicle) => selectedVehicleIds.includes(vehicle._id))
  const passengerCount = form.participants.length + 1
  const suggestionsKey =
    availabilityKey && physicalDestinations.some((location) => location.value === form.destination)
      ? JSON.stringify({
          ...JSON.parse(availabilityKey),
          destination: form.destination,
          participants: form.participants.map((person) => person.value)
        })
      : ''
  const suggestionsReady = Boolean(suggestionsKey) && suggestions.key === suggestionsKey && !suggestions.loading && !suggestions.error
  const suggestionsLoading = Boolean(suggestionsKey) && (suggestions.key !== suggestionsKey || suggestions.loading)
  const destinationConflict = suggestionsReady && suggestions.data.sameDestination.length > 0
  const passengerSeats = selectedVehicles.reduce((total, vehicle) => total + vehicle.passenger_seats, 0)
  const canAddVehicle =
    availabilityReady &&
    form.vehicleIds.length < 5 &&
    selectedVehicles.length === form.vehicleIds.length &&
    selectedVehicles.length < availableVehicles.length
  let capacityError = ''
  if (availabilityReady && selectedVehicleIds.length > 0) {
    if (selectedVehicles.length !== selectedVehicleIds.length) {
      capacityError = 'Zgjidhni automjete të regjistruara, pa përsëritje.'
    } else if (
      !Number.isSafeInteger(passengerSeats) ||
      selectedVehicles.some((vehicle) => !Number.isSafeInteger(vehicle.passenger_seats) || vehicle.passenger_seats < 0)
    ) {
      capacityError = 'Kapaciteti i një automjeti nuk është i vlefshëm.'
    } else if (passengerCount > passengerSeats) {
      capacityError = `Grupi ka ${passengerCount} pasagjerë, por automjetet kanë ${passengerSeats} vende për pasagjerë. Mungojnë ${
        passengerCount - passengerSeats
      } vende. Shtoni një automjet ose ndryshoni pjesëmarrësit.`
    }
  }

  useEffect(() => {
    if (!availabilityKey) return
    let active = true
    setAvailability({ key: availabilityKey, vehicles: [], loading: true, error: '' })
    axios
      .get(isEditing ? `${managementPath}/availability` : 'vehicle-reservations/availability', { params: JSON.parse(availabilityKey) })
      .then(({ data }) => {
        if (!data.success || !Array.isArray(data.data)) throw new Error('Availability unavailable')
        if (!active) return
        setAvailability({ key: availabilityKey, vehicles: data.data, loading: false, error: '' })
        setForm((previous) => ({
          ...previous,
          vehicleIds: previous.vehicleIds.map((id) =>
            data.data.some((vehicle) => vehicle._id === id && vehicle.available === true) ? id : ''
          )
        }))
      })
      .catch(() => {
        if (active)
          setAvailability({
            key: availabilityKey,
            vehicles: [],
            loading: false,
            error: 'Disponueshmëria e automjeteve nuk u ngarkua. Provoni përsëri.'
          })
      })
    return () => {
      active = false
    }
  }, [availabilityKey, availabilityReload, isEditing, managementPath])

  useEffect(() => {
    if (!suggestionsKey) return
    let active = true
    const request = axios.CancelToken.source()
    setSuggestions({ key: suggestionsKey, data: null, loading: true, error: '' })
    axios
      .get(isEditing ? `${managementPath}/suggestions` : 'vehicle-reservations/suggestions', {
        params: JSON.parse(suggestionsKey),
        cancelToken: request.token
      })
      .then(({ data }) => {
        if (!data.success || !Array.isArray(data.data?.sameDestination) || !Array.isArray(data.data?.otherDestinations))
          throw new Error('Suggestions unavailable')
        if (active) setSuggestions({ key: suggestionsKey, data: data.data, loading: false, error: '' })
      })
      .catch((error) => {
        if (active)
          setSuggestions({
            key: suggestionsKey,
            data: null,
            loading: false,
            error: error.response?.data?.message || 'Udhëtimet e sugjeruara nuk u ngarkuan. Provoni përsëri.'
          })
      })
    return () => {
      active = false
      request.cancel('Reservation suggestions changed or closed')
    }
  }, [suggestionsKey, suggestionsReload, isEditing, managementPath])

  useEffect(() => {
    if (isEditing || !currentUser.user_id) return
    let active = true
    setOwnerLoading(true)
    axios
      .get(`users/${currentUser.user_id}`)
      .then(({ data }) => {
        if (!active) return
        if (!data.success || !data.data) throw new Error('Profile unavailable')
        const name = [data.data.first_name, data.data.last_name].filter(Boolean).join(' ')
        setOwnerName(name || currentUser.email || '')
      })
      .catch(() => {
        if (active) setOwnerError('Profili nuk u ngarkua. Po përdoret emaili i llogarisë suaj.')
      })
      .finally(() => {
        if (active) setOwnerLoading(false)
      })
    return () => {
      active = false
    }
  }, [currentUser, isEditing])

  const updateField = (field, value) => {
    if (savingRef.current) return
    setForm((previous) => ({ ...previous, [field]: value }))
    setErrors((previous) => ({
      ...previous,
      [field]: undefined,
      ...(field === 'participants' || ['departureDate', 'departureTime', 'returnDate', 'returnTime'].includes(field)
        ? { vehicleIds: undefined }
        : {})
    }))
    setFormError('')
  }

  const selectVehicle = (index, value) => {
    if (!availableVehicles.some((vehicle) => vehicle._id === value)) return
    if (form.vehicleIds.some((id, position) => position !== index && id === value)) return
    updateField(
      'vehicleIds',
      form.vehicleIds.map((id, position) => (position === index ? value : id))
    )
  }

  const addVehicle = () => {
    if (canAddVehicle) updateField('vehicleIds', [...form.vehicleIds, ''])
  }

  const removeVehicle = (index) => {
    updateField(
      'vehicleIds',
      form.vehicleIds.filter((id, position) => position !== index)
    )
  }

  const loadParticipants = async (input) => {
    const search = input.trim()
    if (search.length === 1) return []
    setParticipantError('')
    try {
      const { data } = await axios.get(isEditing ? `${managementPath}/participants` : 'vehicle-reservations/participants', {
        params: { search }
      })
      if (!data.success || !Array.isArray(data.data)) throw new Error('Employees unavailable')
      return data.data
        .filter((employee) => employee._id && employee._id !== ownerId)
        .map((employee) => ({
          value: employee._id,
          label: `${employee.first_name} ${employee.last_name}${employee.email ? ` (${employee.email})` : ''}`
        }))
    } catch {
      setParticipantError('Punonjësit nuk u ngarkuan. Provoni përsëri me një kërkim të ri.')
      return []
    }
  }

  const saveReservation = async (event) => {
    event.preventDefault()
    if (savingRef.current) return
    const nextErrors = {}
    const date = moment(form.departureDate)
    const returnDate = moment(form.returnDate)
    const departureTime = moment(form.departureTime)
    const returnTime = moment(form.returnTime)
    const unchangedDeparture =
      isEditing && date.format('YYYY-MM-DD') === reservation.departureDate && departureTime.format('HH:mm') === reservation.departureTime

    if (!ownerId || !ownerName) nextErrors.owner = 'Profili i rezervuesit është i nevojshëm.'
    if (!form.departureDate || !date.isValid()) {
      nextErrors.departureDate = 'Zgjidhni një datë të vlefshme.'
    } else if (!unchangedDeparture && (date.isBefore(today, 'day') || date.isAfter(lastBookingDate, 'day'))) {
      nextErrors.departureDate = 'Rezervimi lejohet vetëm nga sot deri në 5 ditë kalendarike përpara.'
    }
    if (!form.departureTime || !departureTime.isValid()) {
      nextErrors.departureTime = 'Shënoni një orë të vlefshme nisjeje.'
    } else if (form.departureDate && date.isValid() && !nextErrors.departureDate) {
      const departure = moment(`${date.format('YYYY-MM-DD')} ${departureTime.format('HH:mm')}`, 'YYYY-MM-DD HH:mm', true)
      if (!unchangedDeparture && departure.isBefore(moment().startOf('minute')))
        nextErrors.departureTime = 'Ora e nisjes nuk mund të jetë në të kaluarën.'
    }
    if (!form.returnDate || !returnDate.isValid()) {
      nextErrors.returnDate = 'Zgjidhni një datë të vlefshme kthimi.'
    } else if (form.departureDate && date.isValid() && returnDate.isBefore(date, 'day')) {
      nextErrors.returnDate = 'Data e kthimit nuk mund të jetë para datës së nisjes.'
    }
    if (!form.returnTime || !returnTime.isValid()) {
      nextErrors.returnTime = 'Shënoni një orë të vlefshme kthimi.'
    } else if (
      form.returnDate &&
      returnDate.isValid() &&
      !nextErrors.returnDate &&
      form.departureDate &&
      date.isValid() &&
      form.departureTime &&
      departureTime.isValid()
    ) {
      const departure = moment(`${date.format('YYYY-MM-DD')} ${departureTime.format('HH:mm')}`, 'YYYY-MM-DD HH:mm', true)
      const returnAt = moment(`${returnDate.format('YYYY-MM-DD')} ${returnTime.format('HH:mm')}`, 'YYYY-MM-DD HH:mm', true)
      if (!returnAt.isAfter(departure)) nextErrors.returnTime = 'Kthimi duhet të jetë pas nisjes.'
    }
    if (!physicalDestinations.some((location) => location.value === form.destination))
      nextErrors.destination = 'Zgjidhni një lokacion fizik të SPEEEX.'
    else if (suggestionsKey && !suggestionsReady) nextErrors.destination = 'Prisni kontrollin e udhëtimeve ose provoni përsëri.'
    else if (destinationConflict)
      nextErrors.destination = 'Për këtë lokacion dhe orar ekziston një udhëtim i miratuar. Bashkohuni udhëtimit ekzistues.'
    if (
      !availabilityReady ||
      form.vehicleIds.length === 0 ||
      form.vehicleIds.length > 5 ||
      form.vehicleIds.some((id) => !id) ||
      selectedVehicles.length !== form.vehicleIds.length
    ) {
      nextErrors.vehicleIds = 'Zgjidhni automjet për çdo fushë ose largoni fushën shtesë. Lejohen deri në 5 automjete të ndryshme.'
    } else if (capacityError) {
      nextErrors.vehicleIds = capacityError
    }
    if (form.exceptionalCase && (!form.exceptionReason.trim() || form.exceptionReason.trim().length > 2000))
      nextErrors.exceptionReason = 'Shënoni arsyen e rastit të jashtëzakonshëm (deri në 2000 karaktere).'

    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) return
    savingRef.current = true
    setSaving(true)
    setFormError('')
    let savedReservation
    try {
      const payload = {
        departureDate: date.format('YYYY-MM-DD'),
        departureTime: departureTime.format('HH:mm'),
        returnDate: returnDate.format('YYYY-MM-DD'),
        returnTime: returnTime.format('HH:mm'),
        destination: form.destination.trim(),
        vehicleIds: form.vehicleIds,
        participants: form.participants.map((participant) => participant.value),
        exceptionalCase: form.exceptionalCase,
        exceptionReason: form.exceptionalCase ? form.exceptionReason.trim() : '',
        ...(isEditing ? { revision: reservation.revision || 0 } : {})
      }
      const { data } = isEditing ? await axios.put(managementPath, payload) : await axios.post('vehicle-reservations', payload)
      if (!data.success || !data.data) {
        setFormError(data.message || 'Kërkesa nuk u ruajt. Provoni përsëri.')
        setErrors(data.errors || {})
      } else savedReservation = data.data
    } catch (error) {
      setFormError(error.response?.data?.message || 'Kërkesa nuk u ruajt. Provoni përsëri.')
      setErrors(error.response?.data?.errors || {})
      if (error.response?.status === 409 && error.response?.data?.errors?.vehicleIds) setAvailabilityReload((previous) => previous + 1)
      if (error.response?.status === 409 && error.response?.data?.errors?.destination) setSuggestionsReload((previous) => previous + 1)
    } finally {
      savingRef.current = false
      setSaving(false)
    }
    if (savedReservation) onSaved(savedReservation)
  }

  const joinSuggestedTrip = async (trip) => {
    if (savingRef.current || !suggestionsReady || !trip.canJoin) return
    savingRef.current = true
    setSaving(true)
    setJoiningId(trip._id)
    setFormError('')
    let joined
    try {
      const { data } = await axios.post(`vehicle-reservations/${trip._id}/join-with-group`, {
        participants: form.participants.map((person) => person.value)
      })
      if (!data.success || !data.data?._id) throw new Error('Join unavailable')
      joined = data.data
    } catch (error) {
      setFormError(error.response?.data?.message || 'Grupi nuk u bashkua. Provoni përsëri.')
      setSuggestionsReload((previous) => previous + 1)
      setAvailabilityReload((previous) => previous + 1)
    } finally {
      savingRef.current = false
      setSaving(false)
      setJoiningId(null)
    }
    if (joined) {
      if (onJoined) onJoined(joined)
      else onClose()
    }
  }

  return (
    <Modal
      show
      onHide={() => {
        if (!savingRef.current) onClose()
      }}
      backdrop={saving ? 'static' : true}
      keyboard={!saving}
      className='modal-1'
      size='lg'
      aria-labelledby='vehicle-reservation-form-title'
    >
      <Modal.Header closeButton={!saving}>
        <Modal.Title id='vehicle-reservation-form-title'>{isEditing ? 'Modifiko rezervimin' : 'Kërko rezervim'}</Modal.Title>
      </Modal.Header>
      <form onSubmit={saveReservation} noValidate aria-busy={saving} className={classes.form}>
        <Modal.Body>
          <Typography variant='body2' color='textSecondary'>
            {isEditing
              ? 'Përditësoni të dhënat e udhëtimit. Statusi aktual i rezervimit do të ruhet.'
              : 'Plotësoni të dhënat e udhëtimit. Rezervimi lejohet deri në 5 ditë kalendarike përpara. Të gjitha kërkesat presin miratim.'}
          </Typography>
          <Box className={classes.fields}>
            <TextField
              id='reservation-owner'
              disabled={saving}
              label='Rezervuesi'
              variant='outlined'
              fullWidth
              required
              value={ownerName}
              InputProps={{ readOnly: true }}
              error={Boolean(errors.owner)}
              helperText={
                errors.owner ||
                (ownerLoading
                  ? 'Duke ngarkuar profilin…'
                  : ownerError || (isEditing ? 'Profili i rezervuesit.' : 'Profili i përdoruesit të kyçur.'))
              }
              className={classes.fullWidth}
            />
            <DatePicker
              label='Data e nisjes'
              disabled={saving}
              inputFormat='dd/MM/yyyy'
              value={form.departureDate}
              minDate={
                isEditing && moment(reservation.departureDate).isBefore(today, 'day')
                  ? moment(reservation.departureDate).toDate()
                  : today.toDate()
              }
              maxDate={
                isEditing && moment(reservation.departureDate).isAfter(lastBookingDate, 'day')
                  ? moment(reservation.departureDate).toDate()
                  : lastBookingDate.toDate()
              }
              PopperProps={{ disablePortal: true }}
              onChange={(value) => updateField('departureDate', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id='reservation-departure-date'
                  variant='outlined'
                  fullWidth
                  required
                  error={Boolean(errors.departureDate) || params.error}
                  helperText={
                    errors.departureDate ||
                    `${isEditing ? 'Për nisje të re: nga' : 'Nga'} ${today.format('DD/MM/YYYY')} deri më ${lastBookingDate.format(
                      'DD/MM/YYYY'
                    )}.`
                  }
                />
              )}
            />
            <TimePicker
              label='Ora e nisjes'
              disabled={saving}
              ampm={false}
              inputFormat='HH:mm'
              value={form.departureTime}
              onChange={(value) => updateField('departureTime', value)}
              PopperProps={{ disablePortal: true }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id='reservation-departure-time'
                  variant='outlined'
                  fullWidth
                  required
                  error={Boolean(errors.departureTime) || params.error}
                  helperText={errors.departureTime}
                />
              )}
            />
            <DatePicker
              label='Data e kthimit'
              disabled={saving}
              inputFormat='dd/MM/yyyy'
              value={form.returnDate}
              minDate={form.departureDate && moment(form.departureDate).isValid() ? form.departureDate : today.toDate()}
              PopperProps={{ disablePortal: true }}
              onChange={(value) => updateField('returnDate', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id='reservation-return-date'
                  variant='outlined'
                  fullWidth
                  required
                  error={Boolean(errors.returnDate) || params.error}
                  helperText={errors.returnDate}
                />
              )}
            />
            <TimePicker
              label='Ora e kthimit'
              disabled={saving}
              ampm={false}
              inputFormat='HH:mm'
              value={form.returnTime}
              onChange={(value) => updateField('returnTime', value)}
              PopperProps={{ disablePortal: true }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id='reservation-return-time'
                  variant='outlined'
                  fullWidth
                  required
                  error={Boolean(errors.returnTime) || params.error}
                  helperText={errors.returnTime}
                />
              )}
            />
            <Box className={classes.fullWidth}>
              {form.vehicleIds.map((vehicleId, index) => (
                <Box key={index} className={classes.vehicleRow}>
                  <TextField
                    id={index === 0 ? 'reservation-vehicles' : `reservation-vehicles-${index + 1}`}
                    label={`Automjeti ${index + 1}`}
                    select
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'aria-describedby': 'reservation-vehicles-help' }}
                    variant='outlined'
                    fullWidth
                    required
                    disabled={saving || !availabilityReady || vehicles.length === 0}
                    value={vehicleId}
                    onChange={(event) => selectVehicle(index, event.target.value)}
                    error={Boolean(errors.vehicleIds || capacityError)}
                  >
                    <option value='' disabled>
                      Zgjidhni automjetin
                    </option>
                    {vehicles
                      .filter((vehicle) => vehicle._id === vehicleId || !selectedVehicleIds.includes(vehicle._id))
                      .map((vehicle) => (
                        <option key={vehicle._id} value={vehicle._id} disabled={vehicle.available !== true}>
                          {formatVehicleLabel(vehicle)} — {vehicle.make} {vehicle.model} ({vehicle.passenger_seats} vende për pasagjerë)
                          {vehicle.available !== true ? ' — I padisponueshëm' : ''}
                        </option>
                      ))}
                  </TextField>
                  {index > 0 && (
                    <IconButton
                      type='button'
                      aria-label={`Largo fushën e automjetit ${index + 1}`}
                      title='Largo automjetin'
                      disabled={saving}
                      onClick={() => removeVehicle(index)}
                      className={classes.vehicleAction}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                  {index === form.vehicleIds.length - 1 && (
                    <IconButton
                      type='button'
                      aria-label='Shto automjet'
                      title='Shto automjet'
                      color='primary'
                      disabled={saving || !canAddVehicle}
                      onClick={addVehicle}
                      className={classes.vehicleAddAction}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
              <FormHelperText id='reservation-vehicles-help' error={Boolean(errors.vehicleIds || capacityError || vehiclesError)}>
                {errors.vehicleIds ||
                  capacityError ||
                  vehiclesError ||
                  (!availabilityKey
                    ? 'Zgjidhni datat dhe orët e nisjes dhe kthimit për të parë automjetet e disponueshme.'
                    : vehiclesLoading
                      ? 'Duke kontrolluar disponueshmërinë…'
                      : availableVehicles.length === 0
                        ? 'Nuk ka automjete të disponueshme për këtë orar. Provoni një orar tjetër.'
                        : selectedVehicleIds.length > 0
                          ? `${passengerCount} pasagjerë / ${passengerSeats} vende për pasagjerë.`
                          : 'Zgjidhni një automjet.')}
              </FormHelperText>
              <FormHelperText>Automjetet mund të rezervohen të paktën 30 minuta pas kthimit nga udhëtimi i mëparshëm.</FormHelperText>
            </Box>
            {vehiclesError && (
              <Button onClick={() => setAvailabilityReload((previous) => previous + 1)} disabled={saving} className={classes.fullWidth}>
                Provo përsëri
              </Button>
            )}
            <TextField
              id='reservation-destination'
              disabled={saving}
              select
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              label='Destinacioni'
              variant='outlined'
              fullWidth
              required
              value={form.destination}
              onChange={(event) => updateField('destination', event.target.value)}
              error={Boolean(errors.destination || destinationConflict)}
              helperText={
                errors.destination ||
                (destinationConflict
                  ? 'Ekziston një udhëtim i miratuar për këtë lokacion dhe orar. Bashkohuni udhëtimit të sugjeruar.'
                  : '')
              }
              className={classes.fullWidth}
            >
              <option value=''>Zgjidhni lokacionin</option>
              {physicalDestinations.map((location) => (
                <option key={location.value} value={location.value}>
                  {location.name}
                </option>
              ))}
            </TextField>
            {suggestionsKey && (
              <Box className={classes.fullWidth}>
                {suggestionsLoading && (
                  <Typography role='status' variant='body2'>
                    Duke kërkuar udhëtime në këtë orar…
                  </Typography>
                )}
                {suggestions.key === suggestionsKey && suggestions.error && (
                  <Box>
                    <Typography role='alert' color='error'>
                      {suggestions.error}
                    </Typography>
                    <Button type='button' disabled={saving} onClick={() => setSuggestionsReload((previous) => previous + 1)}>
                      Provo përsëri
                    </Button>
                  </Box>
                )}
                {suggestionsReady && (
                  <ReservationSuggestions
                    {...suggestions.data}
                    passengerCount={passengerCount}
                    busy={saving}
                    joiningId={joiningId}
                    onJoin={isEditing ? undefined : joinSuggestedTrip}
                  />
                )}
              </Box>
            )}
            <Box className={classes.fullWidth}>
              <label htmlFor='reservation-participants' className={classes.participantLabel}>
                Punonjësit pjesëmarrës (opsionale)
              </label>
              <AsyncSelect
                classNamePrefix='reservation-participants'
                inputId='reservation-participants'
                instanceId='reservation-participants'
                isMulti
                isDisabled={saving}
                isClearable
                cacheOptions
                defaultOptions
                loadOptions={loadParticipants}
                value={form.participants}
                onChange={(value) => updateField('participants', value || [])}
                placeholder='Kërkoni punonjësit…'
                loadingMessage={() => 'Duke kërkuar…'}
                noOptionsMessage={({ inputValue }) =>
                  inputValue.trim().length === 1 ? 'Shkruani të paktën 2 shkronja.' : 'Nuk u gjetën punonjës aktivë.'
                }
                aria-describedby='reservation-participants-help'
              />
              <FormHelperText id='reservation-participants-help' error={Boolean(errors.participants || participantError)}>
                {errors.participants || participantError || `Gjithsej ${passengerCount} pasagjerë, përfshirë rezervuesin.`}
              </FormHelperText>
            </Box>
            <Box className={classes.fullWidth}>
              <FormControlLabel
                control={
                  <Checkbox
                    color='primary'
                    disabled={saving}
                    checked={form.exceptionalCase}
                    onChange={(event) => updateField('exceptionalCase', event.target.checked)}
                  />
                }
                label='Rast i jashtëzakonshëm'
              />
              {form.exceptionalCase && (
                <TextField
                  id='reservation-exception-reason'
                  disabled={saving}
                  inputProps={{ maxLength: 2000 }}
                  label='Arsyeja e rastit të jashtëzakonshëm'
                  variant='outlined'
                  fullWidth
                  required
                  multiline
                  minRows={3}
                  value={form.exceptionReason}
                  onChange={(event) => updateField('exceptionReason', event.target.value)}
                  error={Boolean(errors.exceptionReason)}
                  helperText={errors.exceptionReason || 'Rastet e jashtëzakonshme kërkojnë miratim nga personi i autorizuar.'}
                />
              )}
            </Box>
          </Box>
          {formError && (
            <Typography role='alert' color='error' className={classes.error}>
              {formError}
            </Typography>
          )}
          {errors.revision && onReloadReservation && (
            <Button onClick={onReloadReservation} disabled={saving}>
              Ngarko të dhënat e fundit
            </Button>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onClose} disabled={saving}>
            Mbyll
          </Button>
          <Button
            type='submit'
            variant='contained'
            color='primary'
            disabled={
              saving ||
              ownerLoading ||
              vehiclesLoading ||
              suggestionsLoading ||
              destinationConflict ||
              Boolean(suggestionsKey && !suggestionsReady)
            }
          >
            {saving ? (isEditing ? 'Duke ruajtur…' : 'Duke dërguar…') : isEditing ? 'Ruaj ndryshimet' : 'Dërgo kërkesën'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default ReservationRequestForm
